import { useEffect, useState } from 'react';
import { SxProps, Theme, Typography } from '@mui/material';
import { formatDuration, formatLiveCallDuration } from 'utils/helperFunctions';

interface LiveCallDurationProps {
  status?: string;
  startAt?: string | number | null;
  createdAt?: string | number | null;
  durationInSeconds?: number | null;
  sx?: SxProps<Theme>;
}

const getTimestampMs = (value?: string | number | null): number | null => {
  if (value == null || value === '') return null;

  if (typeof value === 'number') {
    const ms = value < 1e12 ? value * 1000 : value;
    return Number.isFinite(ms) ? ms : null;
  }

  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
};

const getElapsedSeconds = (startedAtMs: number) => Math.max(0, Math.floor((Date.now() - startedAtMs) / 1000));

export default function LiveCallDuration({ status, startAt, createdAt, durationInSeconds, sx }: LiveCallDurationProps) {
  const isOngoing = status?.toLowerCase() === 'ongoing';
  const startedAtMs = getTimestampMs(startAt) ?? getTimestampMs(createdAt);
  const [duration, setDuration] = useState<number | null>(() => {
    if (isOngoing && startedAtMs != null) {
      return getElapsedSeconds(startedAtMs);
    }
    return durationInSeconds ?? null;
  });

  useEffect(() => {
    if (!isOngoing) {
      setDuration(durationInSeconds ?? null);
      return;
    }

    if (startedAtMs == null) {
      setDuration(durationInSeconds ?? null);
      return;
    }

    const syncDuration = () => setDuration(getElapsedSeconds(startedAtMs));

    syncDuration();

    const intervalId = window.setInterval(syncDuration, 1000);

    return () => window.clearInterval(intervalId);
  }, [isOngoing, startedAtMs, durationInSeconds]);

  if (duration === null || duration === undefined) return null;

  if (isOngoing && duration > 600) return null;

  const formattedDuration = isOngoing ? formatLiveCallDuration(duration) : formatDuration(duration);

  return <Typography sx={sx ?? { fontSize: { xs: '14px', lg: '16px' } }}>{formattedDuration}</Typography>;
}
